import { ReactNode, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { useConsent, openCookieBanner } from "@/lib/cookieConsent";

interface Props {
  children: ReactNode;
  service: "Vimeo" | "Calendly" | "OpenStreetMap";
  className?: string;
}

/**
 * SSR/SSG behavior:
 * - Server renders a neutral loading skeleton (no localStorage access).
 * - First client render also renders the skeleton so hydration matches.
 * - After mount, we know the real consent state:
 *     "all" → render the embed (iframe / Calendly / etc.)
 *     other → render the "cookies needed" message
 *
 * The skeleton avoids the returning-consented visitor seeing the
 * "cookies needed" placeholder flash before the embed loads.
 */
export default function ConsentGate({ children, service, className }: Props) {
  const consent = useConsent();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const wrapperClass =
    className ??
    "relative aspect-video w-full rounded-2xl overflow-hidden shadow-elegant bg-primary";

  // Pre-mount: neutral skeleton, identical on server and first client render.
  if (!mounted) {
    return (
      <div className={wrapperClass} aria-hidden="true">
        <div className="absolute inset-0 animate-pulse bg-primary/60" />
      </div>
    );
  }

  if (consent === "all") return <div className={wrapperClass}>{children}</div>;

  const msg =
    service === "Vimeo"
      ? "Um das Video anzuzeigen, benötigen wir Ihre Einwilligung für externe Medien."
      : service === "OpenStreetMap"
      ? "Um die Karte anzuzeigen, benötigen wir Ihre Einwilligung für externe Medien. Beim Laden werden Daten an OpenStreetMap übertragen."
      : "Um den Terminbuchungsservice zu nutzen, benötigen wir Ihre Einwilligung für externe Medien.";

  return (
    <div className={wrapperClass}>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6 gap-4">
        <p className="text-white/90 text-sm sm:text-base max-w-md">{msg}</p>
        <Button
          type="button"
          onClick={openCookieBanner}
          variant="accent"
        >
          Cookie-Einstellungen öffnen
        </Button>
      </div>
    </div>
  );
}
